import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'
import { downloadCsv } from './csv'

/**
 * Kâr/zarar: tahakkuk bazında yevmiyedeki gelir/gider hesapları, nakit bazında tahsilat/ödeme ve kasa hareketleri
 * (aktarımlar hariç). Ödeme planından doğan giderler sabit; personel giderleri ayrı yetkiyle ayrıntılı görünür.
 */
export const PNL_PERM = 'finance:report:pnl'
export const OPENING_POST = 'finance:opening:post'

export type PnLBasis = 'accrual' | 'cash'
/** departmentId: '' tümü, 'none' departmansız; withBudget onaylı bütçeyle karşılaştırma (tahakkuk, bütçe okuma yetkisi). */
export type PnLFilter = { from: string; to: string; basis: PnLBasis; projectId: string; partyId: string; bankId: string; departmentId: string; withBudget: boolean }

export type PnLLine = {
  key: string
  code: string
  name: string
  kind: 'income' | 'expense'
  fixed: boolean
  personnel: boolean
  masked: boolean
  months: Record<string, string>
  total: string
  /** Bütçe karşılaştırması: ay bütçeleri, toplam, fark (gerçekleşen − bütçe) ve fark yüzdesi. */
  budget: Record<string, string> | null
  budgetTotal: string | null
  variance: string | null
  variancePct: number | null
}

export type PnLMonth = {
  period: string
  income: string
  expense: string
  fixedExpense: string
  variableExpense: string
  personnel: string | null
  net: string
  marginPct: number | null
  budgetIncome: string | null
  budgetExpense: string | null
  budgetNet: string | null
}

export type PnLReport = {
  basis: PnLBasis
  from: string
  to: string
  periods: string[]
  income: PnLLine[]
  expense: PnLLine[]
  months: PnLMonth[]
  totals: PnLMonth
  previous: PnLMonth
  previousFrom: string
  previousTo: string
  personnelVisible: boolean
  withBudget: boolean
}

type Row = Record<string, unknown>
const s = (v: unknown) => (v == null ? '' : String(v))

const mapLine = (r: Row): PnLLine => ({
  key: s(r.key),
  code: s(r.code),
  name: s(r.name),
  kind: r.kind === 'income' ? 'income' : 'expense',
  fixed: r.fixed === true,
  personnel: r.personnel === true,
  masked: r.masked === true,
  months: (r.months && typeof r.months === 'object' ? r.months : {}) as Record<string, string>,
  total: s(r.total),
  budget: r.budget && typeof r.budget === 'object' ? (r.budget as Record<string, string>) : null,
  budgetTotal: r.budget_total == null ? null : s(r.budget_total),
  variance: r.variance == null ? null : s(r.variance),
  variancePct: r.variance_pct == null ? null : Number(r.variance_pct),
})

const mapMonth = (r: Row): PnLMonth => ({
  period: s(r.period),
  income: s(r.income),
  expense: s(r.expense),
  fixedExpense: s(r.fixed_expense),
  variableExpense: s(r.variable_expense),
  personnel: r.personnel == null ? null : s(r.personnel),
  net: s(r.net),
  marginPct: r.margin_pct == null ? null : Number(r.margin_pct),
  budgetIncome: r.budget_income == null ? null : s(r.budget_income),
  budgetExpense: r.budget_expense == null ? null : s(r.budget_expense),
  budgetNet: r.budget_net == null ? null : s(r.budget_net),
})

export const usePnL = (f: PnLFilter) =>
  useQuery({
    queryKey: ['finance', 'pnl', f],
    queryFn: async (): Promise<PnLReport> => {
      const r = await api.get<Row>('/api/v1/finance/reports/pnl', {
        from: f.from,
        to: f.to,
        basis: f.basis,
        project_id: f.projectId,
        party_id: f.partyId,
        bank_id: f.basis === 'cash' ? f.bankId : '',
        department_id: f.departmentId,
        budget: f.withBudget && f.basis === 'accrual' ? '1' : '',
      })
      return {
        basis: r.basis === 'cash' ? 'cash' : 'accrual',
        from: s(r.from),
        to: s(r.to),
        periods: Array.isArray(r.periods) ? r.periods.map(s) : [],
        income: asList(r.income as Row[]).map(mapLine),
        expense: asList(r.expense as Row[]).map(mapLine),
        months: asList(r.months as Row[]).map(mapMonth),
        totals: mapMonth((r.totals ?? {}) as Row),
        previous: mapMonth((r.previous ?? {}) as Row),
        previousFrom: s(r.previous_from),
        previousTo: s(r.previous_to),
        personnelVisible: r.personnel_visible === true,
        withBudget: r.with_budget === true,
      }
    },
  })

const csvNum = (v: string | undefined) => Number(v || 0).toFixed(2).replace('.', ',')

/** CSV: satırlar × aylar + toplam; maskeli personel satırı boş tutarla. */
export function exportPnLCsv(rep: PnLReport, labels: { line: string; total: string; income: string; expense: string; net: string; fixed: string; masked: string }) {
  const row = (l: PnLLine) => [
    `${l.code ? `${l.code} ` : ''}${l.name}${l.fixed ? ` (${labels.fixed})` : ''}`,
    ...rep.periods.map((p) => (l.masked ? labels.masked : csvNum(l.months[p]))),
    l.masked ? labels.masked : csvNum(l.total),
  ]
  const sum = (label: string, key: 'income' | 'expense' | 'net') => [label, ...rep.months.map((m) => csvNum(m[key])), csvNum(rep.totals[key])]
  downloadCsv(`kar-zarar-${rep.basis}-${rep.from}_${rep.to}.csv`, [labels.line, ...rep.periods, labels.total], [
    ...rep.income.map(row),
    sum(labels.income, 'income'),
    ...rep.expense.map(row),
    sum(labels.expense, 'expense'),
    sum(labels.net, 'net'),
  ])
}

export type OpeningRow = {
  accountId: string
  name: string
  type: string
  currency: string
  opening: string
  posted: boolean
  stale: boolean
  date: string
  counterCode: string
  amountTry: string
  fxRate: string
  journalId: string
}

export const OPENING_COUNTERS = ['500', '570', '580', '331'] as const

export const useOpenings = () =>
  useQuery({
    queryKey: ['finance', 'openings'],
    queryFn: async () =>
      asList(await api.get<Row[]>('/api/v1/finance/openings')).map(
        (r): OpeningRow => ({
          accountId: s(r.account_id),
          name: s(r.name),
          type: s(r.type),
          currency: s(r.currency) || 'TRY',
          opening: s(r.opening),
          posted: r.posted === true,
          stale: r.stale === true,
          date: s(r.date),
          counterCode: s(r.counter_code),
          amountTry: s(r.amount_try),
          fxRate: s(r.fx_rate),
          journalId: s(r.journal_id),
        }),
      ),
  })

/** Tek hesap (accountId) ya da fişi olmayan/güncel olmayan tüm hesaplar için açılış fişi. */
export const usePostOpening = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ accountId, date, counterCode, fxRate }: { accountId?: string; date: string; counterCode: string; fxRate?: string }) =>
      api.post(accountId ? `/api/v1/finance/openings/${accountId}` : '/api/v1/finance/openings', {
        date,
        counter_code: counterCode,
        fx_rate: fxRate?.trim() ? fxRate.trim().replace(',', '.') : null,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['finance'] }),
  })
}
